/**
 * Entry point. Mirrors experts-web's main.tsx: complete the async bootstrap
 * (config → Sentry → MSAL → configureApi) BEFORE rendering, so the auth gate and
 * the API client are settled on first paint (no flash of /signin while MSAL
 * completes the Entra redirect). A fatal-error fallback renders if bootstrap
 * throws.
 *
 * StrictMode is applied inside AppProviders (buildProvidersTree), so the success
 * branch is NOT double-wrapped; the fatal branch wraps in StrictMode standalone.
 */
import { CheckCircleIcon, XIcon } from '@phosphor-icons/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'sonner';
import App from './app/App';
import { bootstrap } from './app/bootstrap';
import { AppProviders } from './app/providers';
import './styles.css';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');
const root = createRoot(rootEl);

function FatalError({ message }: { message: string }) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-surface-background p-6">
      <div className="max-w-md rounded-lg border border-border-card bg-surface-midground p-6 text-center shadow-elevation-100">
        <h1 className="font-sans font-semibold text-content-danger">
          Unable to start
        </h1>
        <p className="mt-2 font-inter text-content-secondary text-sm">
          {message}
        </p>
      </div>
    </div>
  );
}

bootstrap()
  .then(() => {
    root.render(
      <AppProviders>
        <Toaster
          icons={{
            success: (
              <CheckCircleIcon
                className="size-icon-sm text-content-success"
                weight="fill"
              />
            ),
            error: (
              <XIcon
                className="size-icon-sm text-content-danger"
                weight="fill"
              />
            ),
          }}
          position="bottom-right"
        />
        <App />
      </AppProviders>,
    );
  })
  .catch((err: unknown) => {
    root.render(
      <StrictMode>
        <FatalError
          message={
            err instanceof Error
              ? err.message
              : 'Bootstrap failed unexpectedly.'
          }
        />
      </StrictMode>,
    );
  });
